/**
 * Barion Pixel helper (Base + Full).
 *
 * The Base Pixel snippet is loaded in /app/frontend/public/index.html.
 * This module exposes a thin React-friendly API around the global `window.bp`
 * for emitting the mandatory Full Pixel events:
 *   - contentView    (route changes / product views)
 *   - addToCart      (CheckoutModal opened for a tier)
 *   - initiateCheckout (CheckoutModal submit clicked)
 *   - purchase       (PaymentReturnPage detected Succeeded)
 *
 * Pixel ID is sourced from `window.barion_pixel_id` (injected by index.html).
 * If unavailable (e.g. local dev without env var), all calls become safe no-ops
 * — never throws.
 */

const isClient = () => typeof window !== 'undefined';

const safeBp = (...args) => {
  if (!isClient()) return;
  try {
    if (typeof window.bp !== 'function') return;
    if (!window.barion_pixel_id) return; // not configured -> no-op
    window.bp(...args);
  } catch (_e) {
    /* never crash UI on tracking failure */
  }
};

export const isBarionPixelConfigured = () =>
  isClient() && typeof window.bp === 'function' && !!window.barion_pixel_id;

/**
 * Universal contentView dispatcher.
 *   payload.contentType: 'Page' | 'Product' (default 'Page')
 *   payload.path: optional canonical path
 */
export const trackContentView = (payload = {}) => {
  const data = {
    contentType: 'Page',
    currency:    'HUF',
    ...payload,
  };
  safeBp('track', 'contentView', data);
};

/** addToCart: customer opened a CheckoutModal for a specific tier */
export const trackAddToCart = ({ programId, programTitle, value, currency = 'HUF' } = {}) => {
  safeBp('track', 'addToCart', {
    contentType: 'Product',
    contents:    [{ id: programId, name: programTitle, totalItemPrice: value, quantity: 1 }],
    currency,
    revenue:     value,
  });
};

/** initiateCheckout: submit button on CheckoutModal pressed (before redirect to Barion) */
export const trackInitiateCheckout = ({ programId, programTitle, value, currency = 'HUF' } = {}) => {
  safeBp('track', 'initiateCheckout', {
    contentType: 'Product',
    contents:    [{ id: programId, name: programTitle, totalItemPrice: value, quantity: 1 }],
    currency,
    revenue:     value,
    step:        1,
  });
};

/** purchase: payment-return page confirmed Barion 'Succeeded' */
export const trackPurchase = ({ orderId, programId, programTitle, value, currency = 'HUF' } = {}) => {
  safeBp('track', 'purchase', {
    contentType: 'Product',
    contents:    [{ id: programId, name: programTitle, totalItemPrice: value, quantity: 1 }],
    currency,
    revenue:     value,
    orderId,
  });
};
